import { Suspense, lazy } from 'react'
import type { CustomerConfirmCandidate, RenderPart } from '@/lib/render'
import { CalendarRenderer } from './renderers/CalendarRenderer'
import { BookingListRenderer } from './renderers/BookingListRenderer'
import { TableRenderer } from './renderers/TableRenderer'
import { BookingCardRenderer } from './renderers/BookingCardRenderer'
import { CustomerConfirmRenderer } from './renderers/CustomerConfirmRenderer'

// Charts are the one renderer worth deferring: Recharts is ~405 kB raw, and this
// dispatch is already dynamic — a conversation only pays for it once the AI
// actually returns a chart part. Everything else here is small enough that a
// loading flicker would cost more than the bytes save.
const ChartRenderer = lazy(() =>
  import('./renderers/ChartRenderer').then((m) => ({ default: m.ChartRenderer })),
)

interface RenderCanvasProps {
  part: RenderPart
  /**
   * Operator surfaces only. Turns calendar appointment tiles into quick-action
   * menus and booking-list rows into links to the booking record. The customer
   * chat renders the same parts through this same canvas,
   * so this must stay off by default — see ChatInterface, which gates it on the
   * resolved role rather than on which page it happens to be mounted in.
   */
  interactive?: boolean
  /**
   * Lets button-driven renderers (customer_confirm) send the user's choice
   * into the chat as their next message. Supplied by ChatInterface; absent on
   * non-chat surfaces, where those renderers show their content read-only.
   */
  onQuickReply?: (text: string) => void
  /**
   * Operator surfaces only. Reports the customer the operator picked in a
   * renderer (customer_confirm) so the composer's customer pin can follow it.
   */
  onCustomerPick?: (customer: CustomerConfirmCandidate) => void
}

/**
 * RenderCanvas dispatches a RenderPart from the AI to the correct renderer.
 * When the AI calls render_ui({ component: 'calendar', props: {...} }),
 * this component renders the appropriate visual.
 *
 * New renderers: create a file in renderers/ and add a case here.
 */
export function RenderCanvas({ part, interactive, onQuickReply, onCustomerPick }: RenderCanvasProps) {
  const content = (() => {
    switch (part.component) {
      case 'calendar':
        return <CalendarRenderer props={part.props} interactive={interactive} />

      case 'booking_list':
        return <BookingListRenderer props={part.props} interactive={interactive} />

      case 'customer_confirm':
        return (
          <CustomerConfirmRenderer
            props={part.props}
            onQuickReply={onQuickReply}
            onCustomerPick={onCustomerPick}
          />
        )

      case 'table':
        return <TableRenderer props={part.props} />

      case 'chart':
        return (
          <Suspense fallback={<div className="h-[200px] animate-pulse rounded-xl bg-muted" />}>
            <ChartRenderer props={part.props} />
          </Suspense>
        )

      case 'booking_card':
        return <BookingCardRenderer props={part.props} />

      case 'package_summary':
        // Placeholder — implement when AI uses this component
        return <PlaceholderRenderer label="package_summary" props={part.props} />

      case 'timeline':
        // Placeholder — implement when AI uses this component
        return <PlaceholderRenderer label="timeline" props={part.props} />

      default:
        return <PlaceholderRenderer label={part.component} props={part.props} />
    }
  })()

  return (
    <div className="my-1 overflow-hidden rounded-xl">
      {content}
      {part.caption ? (
        <p className="mx-1 mt-1.5 text-xs italic text-muted-foreground">{part.caption}</p>
      ) : null}
    </div>
  )
}

function PlaceholderRenderer({ label, props }: { label: string; props: unknown }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted p-3">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">[{label}]</p>
      <p className="line-clamp-4 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">
        {JSON.stringify(props, null, 2)}
      </p>
    </div>
  )
}
